import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

function normalizeCrownQuickAddPrices(root = document) {
  root
    .querySelectorAll('.crown-quick-add-price .price, .crown-collection-product-card__options-price .price')
    .forEach((price) => {
      const value = price.textContent?.trim() || '';
      if (!value.startsWith('€')) return;
      price.textContent = `${value.replace(/^€\s*/, '')} €`;
    });
}

function scheduleCrownQuickAddPriceNormalization(root = document) {
  requestAnimationFrame(() => normalizeCrownQuickAddPrices(root));
}

function getCrownQuickAddVisiblePrice(root = document) {
  const productPrice = root.querySelector('.crown-quick-add-price');
  if (!(productPrice instanceof HTMLElement)) return '';

  const saleWrapper = productPrice.querySelector('.price__sale');
  const regularWrapper = productPrice.querySelector('.price__regular');
  const activePrice =
    (!saleWrapper?.classList.contains('price__hidden') && saleWrapper?.querySelector('.price')) ||
    (!regularWrapper?.classList.contains('price__hidden') && regularWrapper?.querySelector('.price')) ||
    productPrice.querySelector('.price');

  return activePrice?.textContent?.trim() || '';
}

function syncCrownQuickAddButtonPrice(root = document) {
  const price = getCrownQuickAddVisiblePrice(root);
  const button = root.querySelector('product-form-component .add-to-cart-button');
  const buttonText = button?.querySelector('.add-to-cart-text');
  if (!price || !(buttonText instanceof HTMLElement)) return;

  let buttonPrice = buttonText.querySelector('.crown-quick-add-button-price');
  if (!(buttonPrice instanceof HTMLElement)) {
    buttonPrice = document.createElement('span');
    buttonPrice.className = 'crown-quick-add-button-price';
    buttonPrice.setAttribute('aria-hidden', 'true');
    buttonText.appendChild(buttonPrice);
  }

  buttonPrice.textContent = price;
}

function scheduleCrownQuickAddButtonPriceSync(root = document) {
  requestAnimationFrame(() => syncCrownQuickAddButtonPrice(root));
}

function syncCrownQuickAddChoiceButtons(optionGroup, select) {
  optionGroup.querySelectorAll('.crown-quick-add-choice').forEach((button) => {
    if (!(button instanceof HTMLButtonElement)) return;
    const isSelected = button.dataset.value === select.value;
    button.classList.toggle('is-selected', isSelected);
    button.setAttribute('aria-pressed', String(isSelected));
  });
}

function enhanceCrownQuickAddDropdowns(root = document) {
  root.querySelectorAll('.variant-option--dropdowns').forEach((optionGroup) => {
    if (!(optionGroup instanceof HTMLElement)) return;

    const select = optionGroup.querySelector('select.variant-option__select, select[name^="options["]');
    if (!(select instanceof HTMLSelectElement)) return;

    const existingChoiceList = optionGroup.querySelector('.crown-quick-add-choice-list');
    if (existingChoiceList) {
      syncCrownQuickAddChoiceButtons(optionGroup, select);
      return;
    }

    const choiceList = document.createElement('div');
    choiceList.className = 'crown-quick-add-choice-list';
    choiceList.setAttribute('role', 'group');

    const optionLabel = optionGroup.querySelector(':scope > label, legend')?.textContent?.trim();
    if (optionLabel) choiceList.setAttribute('aria-label', optionLabel);

    Array.from(select.options).forEach((option) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'crown-quick-add-choice';
      button.dataset.value = option.value;
      button.textContent = option.textContent?.trim() || option.value;
      button.disabled = option.disabled;
      button.setAttribute('aria-pressed', String(option.selected));
      button.classList.toggle('is-selected', option.selected);

      button.addEventListener('click', () => {
        if (select.value === option.value) return;
        select.value = option.value;
        option.selected = true;
        syncCrownQuickAddChoiceButtons(optionGroup, select);
        select.dispatchEvent(new Event('change', { bubbles: true }));
      });

      choiceList.appendChild(button);
    });

    select.addEventListener('change', () => syncCrownQuickAddChoiceButtons(optionGroup, select));
    optionGroup.appendChild(choiceList);
  });
}

function scheduleCrownQuickAddDropdownEnhancement(root = document) {
  requestAnimationFrame(() => enhanceCrownQuickAddDropdowns(root));
}

function syncCrownQuickAddSoldOutButtons(root = document) {
  const quickAddContent =
    root instanceof HTMLElement &&
    (root.id === 'quick-add-modal-content' || root.classList.contains('crown-collection-product-card__options'))
      ? root
      : root.querySelector?.('#quick-add-modal-content, .crown-collection-product-card__options');
  if (!(quickAddContent instanceof HTMLElement)) return;

  const soldOutText = quickAddContent.dataset.soldOutText || '';
  const addToCartText = quickAddContent.dataset.addToCartText || '';
  if (!soldOutText && !addToCartText) return;

  quickAddContent.querySelectorAll('.add-to-cart-button').forEach((button) => {
    const label =
      button.querySelector('.add-to-cart-text__content > span > span') ||
      button.querySelector('.add-to-cart-text__content');
    if (!(label instanceof HTMLElement)) return;

    const fallbackText = button.disabled ? soldOutText : addToCartText;
    if (!fallbackText) return;

    const currentText = label.textContent?.trim() || '';
    if (!currentText || (!button.disabled && currentText === soldOutText)) {
      label.textContent = fallbackText;
    }

    if (!button.getAttribute('aria-label') || (!button.disabled && button.getAttribute('aria-label') === soldOutText)) {
      button.setAttribute('aria-label', label.textContent.trim() || fallbackText);
    }
  });
}

function scheduleCrownQuickAddSoldOutButtonSync(root = document) {
  requestAnimationFrame(() => syncCrownQuickAddSoldOutButtons(root));
}

function setCrownQuickAddVisiblePrice(root, price, compareAtPrice = '') {
  if (!price) return;

  const productPrice = root.querySelector('.crown-quick-add-price');
  const priceContainer = productPrice?.querySelector('[ref="priceContainer"]');
  if (!productPrice || !priceContainer) return;

  const regularWrapper = priceContainer.querySelector('.price__regular');
  const saleWrapper = priceContainer.querySelector('.price__sale');
  const regularPrice = regularWrapper?.querySelector('.price') || priceContainer.querySelector('.price');
  const salePrice = saleWrapper?.querySelector('.price');
  const saleComparePrice = saleWrapper?.querySelector('.compare-at-price');

  if (compareAtPrice && compareAtPrice !== price && saleWrapper && salePrice && saleComparePrice) {
    salePrice.textContent = price;
    saleComparePrice.textContent = compareAtPrice;
    regularWrapper?.classList.add('price__hidden');
    saleWrapper.classList.remove('price__hidden');
  } else if (regularPrice) {
    regularPrice.textContent = price;
    regularWrapper?.classList.remove('price__hidden');
    saleWrapper?.classList.add('price__hidden');
  }

  normalizeCrownQuickAddPrices(productPrice);
  syncCrownQuickAddButtonPrice(root);
}

function closeCrownCollectionInlineOptions(exceptCard = null) {
  document
    .querySelectorAll(
      '.crown-collection-product-card--options-open, .crown-collection-product-card--options-loading'
    )
    .forEach((card) => {
      if (card === exceptCard) return;
      card.classList.remove(
        'crown-collection-product-card--options-open',
        'crown-collection-product-card--options-loading'
      );
      card.querySelector('.crown-collection-product-card__options')?.remove();
    });
}

function syncCrownCollectionInlinePanel(panel) {
  normalizeCrownQuickAddPrices(panel);
  syncCrownQuickAddSoldOutButtons(panel);
}

function scheduleCrownCollectionInlinePanelSync(panel) {
  requestAnimationFrame(() => syncCrownCollectionInlinePanel(panel));
  window.setTimeout(() => syncCrownCollectionInlinePanel(panel), 250);
  window.setTimeout(() => syncCrownCollectionInlinePanel(panel), 750);
}

document.addEventListener('keyup', (event) => {
  if (event.key === 'Escape') closeCrownCollectionInlineOptions();
});

document.addEventListener('crown:close-product-card-options', () => closeCrownCollectionInlineOptions());

mediaQueryLarge.addEventListener('change', (event) => {
  if (!event.matches) closeCrownCollectionInlineOptions();
});

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {Promise<Element | null> | null} */
  #pendingContentPromise = null;
  /** @type {string} */
  #pendingContentUrl = '';
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href && !this.dataset.productUrl) return '';

    let url = new URL(productLink?.href || this.dataset.productUrl || '', window.location.origin);
    if (url.origin !== window.location.origin) {
      url = new URL(`${url.pathname}${url.search}`, window.location.origin);
    }

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || this.dataset.selectedVariantId || null;
  }

  #getSelectedVariantOptions() {
    return [
      this.dataset.selectedVariantOption1,
      this.dataset.selectedVariantOption2,
      this.dataset.selectedVariantOption3,
    ].filter((value) => value);
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
    this.addEventListener('pointerenter', this.#handleQuickAddIntent);
    this.addEventListener('focusin', this.#handleQuickAddIntent);
    this.addEventListener('touchstart', this.#handleQuickAddIntent, { passive: true });
    this.addEventListener('pointerdown', this.#stopChooseButtonPointer, { capture: true });
    this.addEventListener('mousedown', this.#stopChooseButtonPointer, { capture: true });
    this.addEventListener('click', this.#handleChooseButtonClick, { capture: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    this.#pendingContentPromise = null;
    this.#pendingContentUrl = '';
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
    this.removeEventListener('pointerenter', this.#handleQuickAddIntent);
    this.removeEventListener('focusin', this.#handleQuickAddIntent);
    this.removeEventListener('touchstart', this.#handleQuickAddIntent);
    this.removeEventListener('pointerdown', this.#stopChooseButtonPointer, { capture: true });
    this.removeEventListener('mousedown', this.#stopChooseButtonPointer, { capture: true });
    this.removeEventListener('click', this.#handleChooseButtonClick, { capture: true });
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
    this.#pendingContentPromise = null;
    this.#pendingContentUrl = '';
  };

  #stopChooseButtonPointer = (event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest('.quick-add__button--choose')) return;
    this.#handleQuickAddIntent();
    event.stopPropagation();
  };

  #handleQuickAddIntent = () => {
    if (this.dataset.quickAddButton !== 'choose') return;
    const currentUrl = this.productPageUrl;
    if (!currentUrl || this.#cachedContent.has(currentUrl)) return;
    void this.#getProductGrid(currentUrl).catch(() => {});
  };

  /**
   * @param {string} productPageUrl
   * @returns {Promise<Element | null>}
   */
  async #getProductGrid(productPageUrl) {
    const cachedProductGrid = this.#cachedContent.get(productPageUrl);
    if (cachedProductGrid) return cachedProductGrid;

    if (!this.#pendingContentPromise || this.#pendingContentUrl !== productPageUrl) {
      this.#pendingContentUrl = productPageUrl;
      this.#pendingContentPromise = this.fetchProductPage(productPageUrl)
        .then((html) => {
          const gridElement = html?.querySelector('[data-product-grid-content]');
          if (!gridElement) return null;
          const productGrid = /** @type {Element} */ (gridElement.cloneNode(true));

          if (!productGrid.querySelector('variant-picker')) {
            const variantPicker = html?.querySelector('[data-product-grid-content] variant-picker, main variant-picker');
            if (variantPicker) productGrid.appendChild(variantPicker.cloneNode(true));
          }

          if (!productGrid.querySelector('product-form-component')) {
            const productForm = html?.querySelector(
              '[data-product-grid-content] product-form-component:not(.quick-add__product-form-component), main product-form-component:not(.quick-add__product-form-component)'
            );
            if (productForm) productGrid.appendChild(productForm.cloneNode(true));
          }

          if (!productGrid.querySelector('product-price')) {
            const productPrice = html?.querySelector('[data-product-grid-content] product-price, main product-price');
            if (productPrice) productGrid.appendChild(productPrice.cloneNode(true));
          }

          this.#cachedContent.set(productPageUrl, productGrid);
          return productGrid;
        })
        .finally(() => {
          if (this.#pendingContentUrl === productPageUrl) {
            this.#pendingContentPromise = null;
            this.#pendingContentUrl = '';
          }
        });
    }

    return this.#pendingContentPromise;
  }

  #handleChooseButtonClick = (event) => {
    if (event.defaultPrevented) return;
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest('.quick-add__button--choose')) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.handleClick(event);
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
    scheduleCrownQuickAddDropdownEnhancement(modalContent);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const sharedProductCard = this.closest('.crown-collection-product-card');
    if (mediaQueryLarge.matches && sharedProductCard instanceof HTMLElement) {
      await this.#openCrownCollectionInlineOptions(sharedProductCard);
      return;
    }

    const currentUrl = this.productPageUrl;

    this.#showQuickAddModalLoading();
    this.#openQuickAddModal();

    const productGrid = await this.#getProductGrid(currentUrl);

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }
  };

  /**
   * Replaces a shared CROWN product card with its option picker on desktop.
   * @param {HTMLElement} card
   */
  async #openCrownCollectionInlineOptions(card) {
    const productPageUrl = this.productPageUrl;
    if (!productPageUrl || card.classList.contains('crown-collection-product-card--options-open')) return;

    closeCrownCollectionInlineOptions(card);
    card.classList.add('crown-collection-product-card--options-loading');

    try {
      const productGrid = await this.#getProductGrid(productPageUrl);
      if (!productGrid) throw new Error('Product content is unavailable.');

      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      const variantPicker = freshContent.querySelector('variant-picker');
      const productPrice = freshContent.querySelector('product-price');
      const productForm = freshContent.querySelector(
        'product-form-component:not(.quick-add__product-form-component)'
      );
      if (!productForm) throw new Error('Product form is unavailable.');

      const panel = document.createElement('div');
      panel.className = 'crown-collection-product-card__options';
      panel.dataset.soldOutText = this.dataset.soldOutText || '';
      panel.dataset.addToCartText = this.dataset.addToCartText || '';

      const heading = document.createElement('h3');
      heading.textContent = this.dataset.loadingText || '';

      const title = document.createElement('a');
      title.className = 'crown-collection-product-card__options-title';
      title.href = productPageUrl;
      title.textContent = this.dataset.productTitle || '';

      const viewProductLink = document.createElement('a');
      viewProductLink.className = 'crown-collection-product-card__options-link';
      viewProductLink.href = productPageUrl;
      viewProductLink.textContent = this.dataset.viewProductText || '';

      panel.append(heading, title);
      if (variantPicker) panel.appendChild(variantPicker);
      if (productPrice) {
        productPrice.classList.add('crown-collection-product-card__options-price');
        panel.appendChild(productPrice);
      }
      panel.append(viewProductLink, productForm);
      card.appendChild(panel);
      card.classList.remove('crown-collection-product-card--options-loading');
      card.classList.add('crown-collection-product-card--options-open');

      this.#syncVariantSelection(panel);
      syncCrownCollectionInlinePanel(panel);
      panel.addEventListener('variant:update', () => scheduleCrownCollectionInlinePanelSync(panel));
      panel.addEventListener('change', () => scheduleCrownCollectionInlinePanelSync(panel));
    } catch (error) {
      card.classList.remove('crown-collection-product-card--options-loading');
      console.warn('[CROWN] Unable to open inline product options.', error);
    }
  }

  #showQuickAddModalLoading() {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    const productTitle = document.createElement('a');
    productTitle.textContent = this.dataset.productTitle || '';
    productTitle.href = this.productPageUrl;

    const productHeader = document.createElement('div');
    productHeader.classList.add('product-header');
    productHeader.appendChild(productTitle);

    const loading = document.createElement('div');
    loading.className = 'crown-quick-add-loading';
    loading.setAttribute('role', 'status');
    loading.setAttribute('aria-live', 'polite');
    loading.setAttribute('aria-label', this.dataset.loadingText || this.dataset.viewProductText || '');
    loading.innerHTML = '<span></span><span></span><span></span>';

    modalContent.replaceChildren(productHeader, loading);
    modalContent.className = 'quick-add-modal__content crown-quick-add-compact-grid crown-quick-add-compact-grid--loading';
  }

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const fetchProductHtml = async (url) => {
        const response = await fetch(url, {
          signal: this.#abortController.signal,
          headers: {
            Accept: 'text/html,application/xhtml+xml',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
        }

        return new DOMParser().parseFromString(await response.text(), 'text/html');
      };

      let html = await fetchProductHtml(productPageUrl);

      if (!html.querySelector('[data-product-grid-content], product-form-component')) {
        const fallbackUrl = new URL(productPageUrl, window.location.origin);
        if (fallbackUrl.searchParams.has('variant')) {
          fallbackUrl.searchParams.delete('variant');
          html = await fetchProductHtml(fallbackUrl.toString());
        }
      }

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    const productDetails = productGrid.querySelector('.product-details');
    const productMedia = productGrid.querySelector('.product-information__media');
    const productFormComponent = productGrid.querySelector('product-form-component');
    const variantPicker = productGrid.querySelector('variant-picker');
    const productPrice = productGrid.querySelector('product-price');
    const productTitle = document.createElement('a');
    productTitle.textContent = this.dataset.productTitle || '';
    productGrid.dataset.soldOutText = this.dataset.soldOutText || '';
    productGrid.dataset.addToCartText = this.dataset.addToCartText || '';

    // Make product title a link to the product page.
    productTitle.href = this.productPageUrl;

    const productHeader = document.createElement('div');
    productHeader.classList.add('product-header');
    productHeader.appendChild(productTitle);

    productGrid.classList.add('crown-quick-add-compact-grid');
    productGrid.appendChild(productHeader);

    if (variantPicker) {
      productGrid.appendChild(variantPicker);
    }

    if (productPrice) {
      productPrice.classList.add('crown-quick-add-price');
      productGrid.appendChild(productPrice);
    }

    const viewProductLink = document.createElement('a');
    viewProductLink.classList.add('crown-quick-add-view-product');
    viewProductLink.href = this.productPageUrl;
    viewProductLink.textContent = this.dataset.viewProductText || 'View product';
    productGrid.appendChild(viewProductLink);

    if (productFormComponent) {
      productGrid.appendChild(productFormComponent);
    }

    productDetails?.remove();
    productMedia?.remove();

    morph(modalContent, productGrid);
    modalContent.classList.add('crown-quick-add-compact-grid');
    modalContent.classList.remove('crown-quick-add-compact-grid--loading');
    modalContent.dataset.soldOutText = this.dataset.soldOutText || '';
    modalContent.dataset.addToCartText = this.dataset.addToCartText || '';

    this.#syncVariantSelection(modalContent);
    enhanceCrownQuickAddDropdowns(modalContent);
    normalizeCrownQuickAddPrices(modalContent);
    syncCrownQuickAddButtonPrice(modalContent);
    syncCrownQuickAddSoldOutButtons(modalContent);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;
    const selectedVariantOptions = this.#getSelectedVariantOptions();
    const selectedVariantAvailable = this.dataset.selectedVariantAvailable !== 'false';

    const variantIdInput = modalContent.querySelector('product-form-component input[name="id"]');
    if (variantIdInput instanceof HTMLInputElement) {
      variantIdInput.value = selectedVariantId;
      variantIdInput.disabled = !selectedVariantAvailable;
    }

    if (selectedVariantOptions.length) {
      const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
      let syncedOption = false;

      selectedVariantOptions.forEach((optionValue, index) => {
        const fieldset = variantPicker?.querySelector(`fieldset[data-fieldset-index="${index}"]`);
        const radioCandidates =
          fieldset?.querySelectorAll('input[type="radio"]') ||
          variantPicker?.querySelectorAll('fieldset input[type="radio"]') ||
          [];
        const radio = Array.from(radioCandidates).find(
          (input) => input instanceof HTMLInputElement && input.value === optionValue
        );

        if (radio instanceof HTMLInputElement) {
          if (!radio.checked) {
            variantPicker?.updateSelectedOption(radio);
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          }
          syncedOption = true;
          return;
        }

        const select = Array.from(
          variantPicker?.querySelectorAll('select.variant-option__select, select[name^="options["]') || []
        ).find((item) => Array.from(item.options || []).some((optionItem) => optionItem.value === optionValue));
        const option = Array.from(select?.options || []).find((item) => item.value === optionValue);

        if (select instanceof HTMLSelectElement && option instanceof HTMLOptionElement) {
          if (select.value !== option.value || !option.selected) {
            select.value = option.value;
            option.selected = true;
            variantPicker?.updateSelectedOption(select);
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
          syncedOption = true;
        }
      });

      if (syncedOption) return;
    }

    // Find and check the corresponding radio input in the modal.
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
    }

    const modalOption = modalContent.querySelector(`select option[data-variant-id="${selectedVariantId}"]`);
    if (!(modalOption instanceof HTMLOptionElement)) return;

    const modalSelect = modalOption.closest('select');
    if (!(modalSelect instanceof HTMLSelectElement) || modalSelect.value === modalOption.value) return;

    modalSelect.value = modalOption.value;
    modalOption.selected = true;
    modalSelect.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal });
    this.addEventListener(ThemeEvents.variantSelected, this.#updatePriceFromSelectedOption, { signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink, { signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#normalizePrices, { signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#enhanceDropdowns, { signal });
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  #normalizePrices = () => {
    scheduleCrownQuickAddPriceNormalization(this);
    scheduleCrownQuickAddButtonPriceSync(this);
    scheduleCrownQuickAddSoldOutButtonSync(this);
  };

  #enhanceDropdowns = () => {
    scheduleCrownQuickAddDropdownEnhancement(this);
  };

  #updatePriceFromSelectedOption = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (!event.target.closest('.crown-quick-add-compact-grid')) return;

    const optionValueId = event.detail?.resource?.id;
    const selectedOption = Array.from(event.target.querySelectorAll('[data-option-value-id]')).find(
      (option) => option instanceof HTMLElement && option.dataset.optionValueId === optionValueId
    );

    if (!(selectedOption instanceof HTMLElement)) return;

    setCrownQuickAddVisiblePrice(
      this,
      selectedOption.dataset.variantPrice || '',
      selectedOption.dataset.variantCompareAtPrice || ''
    );
  };

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
